import { Address, ChangeAddress, AddressType, HousingOccupancyStatus, isCustomerType } from 'cpfr-common';
import { NotificationStatus, useAnalyticsPageViewTracker } from '@cp-shared-19/frontend-ui';
import { DataOverview, Notification } from '@vwfs-bronson/bronson-react';
import { changeBillingAddressPath } from 'components/navigation/paths';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { emptyObjectOrAllValuesAreUndefined } from 'utils';
import { EditStatus, EditViewName } from '../enums';
import { FileUploadView } from './file-upload-view/FileUploadView';
import { FormView } from './form-view/FormView';

export type EditViewProps = {
    address?: Address;
    customerType: isCustomerType;
    cancelEditing: () => void;
    finishEditing: (newEditStatus: EditStatus, updatedAddressDetails?: Address) => void;
    hasBallonContracts?: boolean;
    attempts: number;
};

export const EditView: React.FC<EditViewProps> = ({
    cancelEditing,
    address,
    customerType,
    finishEditing,
    hasBallonContracts,
    attempts,
}) => {
    const { t } = useTranslation('my-profile');
    const currentPathName = useLocation().pathname;
    const [currentView, setCurrentView] = useState(EditViewName.FORM_VIEW);

    const trackingSection = 'Address';
    useAnalyticsPageViewTracker('editProfileSectionDisplayed', true, trackingSection);

    const isBillingAddress = (): boolean => {
        return currentPathName === changeBillingAddressPath();
    };

    const initialValues: ChangeAddress = {
        houseNumber: address?.houseNumber ? address?.houseNumber : '',
        street: address?.street ? address?.street : '',
        streetAdd: address?.streetAdd ? address?.streetAdd : '',
        building: address?.building ? address?.building : '',
        door: address?.door ? address?.door : '',
        postalCode: address?.postalCode ? address?.postalCode : '',
        city: address?.city ? address?.city : '',
        country: address?.country ? address.country : 'France',
        documents: [],
        addressType: isBillingAddress() ? AddressType.INVOICE : AddressType.POSTAL,
        housingOccupancyStatus: HousingOccupancyStatus.HOSTED_FREE,
        attempts: attempts,
    };

    const [addressChangeValues, setAddressChangeValues] = useState<ChangeAddress>(initialValues);

    const closeButtonProps = {
        onClick: (e: React.MouseEvent) => {
            e.preventDefault();
            cancelEditing();
        },
        className: 'c-icon  c-icon--[semantic-close]',
        testId: 'x-button',
    };

    const getEditViewTitle = (): string => {
        return isBillingAddress()
            ? // eslint-disable-next-line
              emptyObjectOrAllValuesAreUndefined(address as object)
                ? t('address.edit-view.title-sme-add-address')
                : t('address.edit-view.title-sme')
            : t('address.edit-view.title');
    };

    return (
        <DataOverview
            href=""
            titleLevel={2}
            title={getEditViewTitle()}
            buttonLabel={' '}
            buttonProps={closeButtonProps}
            id="address-section-edit-view-id"
        >
            <>
                <Notification
                    status={NotificationStatus.warning}
                    className={'u-mb'}
                    title={''}
                    hideTitle={true}
                    visible
                    showCloseButton={false}
                >
                    {t('address.edit-view.info')}
                </Notification>
                {currentView === EditViewName.FILE_UPLOAD_VIEW && (
                    <FileUploadView
                        cancelEditing={cancelEditing}
                        finishEditing={finishEditing}
                        setCurrentView={setCurrentView}
                        addressChangeValues={addressChangeValues}
                        hasBallonContracts={hasBallonContracts}
                        customerType={customerType}
                        isBillingAddress={isBillingAddress()}
                    />
                )}
                {currentView === EditViewName.FORM_VIEW && (
                    <FormView
                        cancelEditing={cancelEditing}
                        finishEditing={finishEditing}
                        setCurrentView={setCurrentView}
                        setAddressChangeValues={setAddressChangeValues}
                        addressChangeValues={addressChangeValues}
                    />
                )}
            </>
        </DataOverview>
    );
};
